import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import SubscriptionPlanCard from './components/SubscriptionPlanCard';
import RegistrationForm from './components/RegistrationForm';
import PlanComparisonTable from './components/PlanComparisonTable';
import ValuePropositionSection from './components/ValuePropositionSection';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const Register = () => {
  const [selectedPlan, setSelectedPlan] = useState('dealer_pro');
  const [currentStep, setCurrentStep] = useState('plans'); // 'plans', 'form', 'success'
  const [isLoading, setIsLoading] = useState(false);
  const [showComparison, setShowComparison] = useState(false);

  const subscriptionPlans = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      description: 'Perfect for occasional crane valuations',
      limits: '3 valuations per month • Basic reports only',
      features: [
        { text: '3 crane valuations per month', included: true },
        { text: 'Basic PDF reports', included: true },
        { text: 'Community support', included: true },
        { text: 'Inventory management', included: false },
        { text: 'Price alerts', included: false },
        { text: 'Bulk uploads', included: false },
        { text: 'API access', included: false }
      ]
    },
    {
      id: 'dealer_pro',
      name: 'Dealer Pro',
      price: 999,
      description: 'Unlimited valuations for crane dealers',
      trial: '14-day free trial',
      features: [
        { text: 'Unlimited crane valuations', included: true },
        { text: 'Professional PDF reports', included: true },
        { text: 'Inventory management system', included: true },
        { text: 'Underpriced inventory alerts', included: true },
        { text: 'Bulk CSV uploads', included: true },
        { text: 'Priority email support', included: true },
        { text: 'Basic API access', included: true },
        { text: 'Custom branding options', included: true }
      ]
    },
    {
      id: 'lender_pro',
      name: 'Lender Pro',
      price: 3000,
      description: 'Complete solution for financial institutions',
      trial: '14-day free trial',
      features: [
        { text: 'Everything in Dealer Pro', included: true },
        { text: 'Insurance-grade reports', included: true },
        { text: 'Portfolio management tools', included: true },
        { text: 'Batch processing capabilities', included: true },
        { text: 'Advanced LTV calculations', included: true },
        { text: 'Dedicated account manager', included: true },
        { text: 'Full API access', included: true },
        { text: 'White-label options', included: true }
      ]
    }
  ];

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
  };

  const handleContinueToForm = () => {
    setCurrentStep('form');
  };

  const handleFormSubmit = async (formData) => {
    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock registration logic
      console.log('Registration data:', formData);
      
      // Redirect based on plan
      if (formData?.selectedPlan === 'free') {
        // Direct account creation for free plan
        setCurrentStep('success');
        setTimeout(() => {
          window.location.href = '/crane-search-dashboard';
        }, 3000);
      } else {
        // Stripe integration for paid plans (mock)
        console.log('Redirecting to Stripe for plan:', formData?.selectedPlan);
        setCurrentStep('success');
        setTimeout(() => {
          window.location.href = '/crane-search-dashboard';
        }, 3000);
      }
    } catch (error) {
      console.error('Registration error:', error);
      alert('Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToPlans = () => {
    setCurrentStep('plans');
  };

  const selectedPlanData = subscriptionPlans?.find(plan => plan?.id === selectedPlan);

  if (currentStep === 'success') {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)] px-4">
          <div className="max-w-md w-full text-center">
            <div className="w-16 h-16 bg-success rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="Check" size={32} className="text-success-foreground" />
            </div>
            <h1 className="text-2xl font-bold text-text-primary mb-4">
              Welcome to CranePrice!
            </h1>
            <p className="text-text-secondary mb-6">
              {selectedPlan === 'free' ?'Your free account has been created successfully. You can now start using CranePrice with 3 free valuations per month.'
                : `Your ${selectedPlanData?.name} trial has been activated. You have 14 days to explore all premium features.`
              }
            </p>
            <div className="flex items-center justify-center space-x-2 text-sm text-text-secondary">
              <Icon name="Clock" size={16} />
              <span>Redirecting to dashboard in a few seconds...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-7xl mx-auto px-4 py-8">
        {currentStep === 'plans' && (
          <div className="space-y-12">
            {/* Value Proposition */}
            <ValuePropositionSection />

            {/* Plan Selection */}
            <div>
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-text-primary mb-4">
                  Choose Your Plan
                </h2>
                <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                  Start with our free plan or unlock advanced features with a professional subscription. 
                  All paid plans include a 14-day free trial.
                </p>
              </div>

              {/* Plan Cards */}
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                {subscriptionPlans?.map((plan) => (
                  <SubscriptionPlanCard
                    key={plan?.id}
                    plan={plan}
                    isSelected={selectedPlan === plan?.id}
                    onSelect={handlePlanSelect}
                    isPopular={plan?.id === 'dealer_pro'}
                  />
                ))}
              </div>

              {/* Plan Comparison Toggle */}
              <div className="text-center mb-8">
                <Button
                  variant="outline"
                  onClick={() => setShowComparison(!showComparison)}
                  iconName={showComparison ? "ChevronUp" : "ChevronDown"}
                  iconPosition="right"
                >
                  {showComparison ? "Hide" : "Compare"} Plan Features
                </Button>
              </div>

              {/* Plan Comparison Table */}
              {showComparison && (
                <div className="mb-8">
                  <PlanComparisonTable
                    onPlanSelect={handlePlanSelect}
                    selectedPlan={selectedPlan}
                  />
                </div>
              )}

              {/* Continue Button */}
              <div className="text-center">
                <Button
                  variant="default"
                  size="lg"
                  onClick={handleContinueToForm}
                  iconName="ArrowRight"
                  iconPosition="right"
                  className="px-8"
                >
                  Continue with {selectedPlanData?.name} Plan
                </Button>
                <p className="text-sm text-text-secondary mt-2">
                  {selectedPlan === 'free' ?'No credit card required for free plan' :'Start your 14-day free trial • Cancel anytime'
                  }
                </p>
              </div>
            </div>
          </div>
        )}

        {currentStep === 'form' && (
          <div className="max-w-4xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Form Section */}
              <div>
                <div className="mb-6">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBackToPlans}
                    iconName="ArrowLeft"
                    iconPosition="left"
                    className="mb-4"
                  >
                    Back to Plans
                  </Button>
                  <h1 className="text-3xl font-bold text-text-primary mb-2">
                    Create Your Account
                  </h1>
                  <p className="text-text-secondary">
                    Join thousands of industry professionals using CranePrice for accurate crane valuations.
                  </p>
                </div>

                <RegistrationForm
                  selectedPlan={selectedPlan}
                  onSubmit={handleFormSubmit}
                  isLoading={isLoading}
                />
              </div>

              {/* Selected Plan Summary */}
              <div className="lg:pl-8">
                <div className="sticky top-8">
                  <div className="bg-surface border border-border rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-text-primary mb-4">
                      Plan Summary
                    </h3>
                    
                    <div className="space-y-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium text-text-primary">{selectedPlanData?.name}</h4>
                          <p className="text-sm text-text-secondary">{selectedPlanData?.description}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-bold text-text-primary">
                            ${selectedPlanData?.price}
                          </div>
                          {selectedPlanData?.price > 0 && (
                            <div className="text-sm text-text-secondary">/month</div>
                          )}
                        </div>
                      </div>

                      {selectedPlanData?.trial && (
                        <div className="bg-success/10 border border-success/20 rounded-md p-3">
                          <div className="flex items-center space-x-2">
                            <Icon name="Gift" size={16} className="text-success" />
                            <span className="text-sm font-medium text-success">
                              {selectedPlanData?.trial} included
                            </span>
                          </div>
                          <p className="text-xs text-success/80 mt-1">
                            No charges until trial ends • Cancel anytime
                          </p>
                        </div>
                      )}

                      <div className="space-y-2">
                        <h5 className="text-sm font-medium text-text-primary">What's included:</h5>
                        <ul className="space-y-1">
                          {selectedPlanData?.features?.filter(f => f?.included)?.slice(0, 5)?.map((feature, index) => (
                            <li key={index} className="flex items-center space-x-2 text-sm">
                              <Icon name="Check" size={12} className="text-success" />
                              <span className="text-text-secondary">{feature?.text}</span>
                            </li>
                          ))}
                          {selectedPlanData?.features?.filter(f => f?.included)?.length > 5 && (
                            <li className="text-xs text-text-secondary pl-4">
                              +{selectedPlanData?.features?.filter(f => f?.included)?.length - 5} more features
                            </li>
                          )}
                        </ul>
                      </div>

                      <div className="pt-4 border-t border-border">
                        <div className="flex items-center space-x-2 text-sm text-text-secondary">
                          <Icon name="Shield" size={14} />
                          <span>Secure checkout • SSL encrypted</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Register;